/**
 * /search — the search experience (C17).
 *
 * The URL is the single source of truth for query, mode, filters, paging, and
 * the opened document; `validateSearch` normalises it via {@link parseSearchParams}.
 * Keyword/semantic results come from {@link useSearch} (TanStack Query); the
 * agentic "Ask" mode streams via {@link useAgentStream}.
 */
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useRef } from "react";
import type { AgentCitation, SearchHit } from "@records-monitor/shared";
import { SaveSearchButton } from "@/features/saved/SaveSearchButton";
import { AgentChat } from "@/features/search/AgentChat";
import { DocumentDrawer, type DocumentDetail } from "@/features/search/DocumentDrawer";
import { Filters, type JurisdictionOption } from "@/features/search/Filters";
import { useAgentStream } from "@/features/search/hooks/useAgentStream";
import { useSearch } from "@/features/search/hooks/useSearch";
import { ModeToggle } from "@/features/search/ModeToggle";
import { ResultsGrid } from "@/features/search/ResultsGrid";
import { SearchBar } from "@/features/search/SearchBar";
import {
  FILTER_KEYS,
  parseSearchParams,
  type SearchMode,
  type SearchParams,
} from "@/features/search/urlState";

// C16 will supply the jurisdiction list from the API; until then the
// multi-select is present (and labelled) but starts empty.
const JURISDICTIONS: readonly JurisdictionOption[] = [];

function hitToDetail(hit: SearchHit): DocumentDetail {
  return {
    documentId: hit.documentId,
    chunkId: hit.chunkId,
    documentTitle: hit.documentTitle,
    jurisdictionName: hit.jurisdictionName,
    bodyName: hit.bodyName,
    meetingDate: hit.meetingDate,
    documentKind: hit.documentKind,
    snippetWithHighlights: hit.snippetWithHighlights,
    sourceUrl: null,
  };
}

function citationToDetail(cite: AgentCitation): DocumentDetail {
  return {
    documentId: cite.documentId,
    chunkId: cite.chunkId,
    documentTitle: cite.documentTitle,
    jurisdictionName: cite.jurisdictionName,
    bodyName: null,
    meetingDate: null,
    documentKind: null,
    snippetWithHighlights: cite.snippet,
    sourceUrl: cite.sourceUrl,
  };
}

function SearchPage(): React.ReactElement {
  const params = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });

  const setParams = useCallback(
    (patch: Partial<SearchParams>): void => {
      void navigate({
        search: (prev) => {
          const merged = { ...(prev as SearchParams), ...patch };
          // Keep only defined keys so cleared values leave the URL entirely.
          return Object.fromEntries(
            Object.entries(merged).filter(([, value]) => value !== undefined),
          ) as SearchParams;
        },
      });
    },
    [navigate],
  );

  // ── Keyword / semantic results ──────────────────────────────────────────
  const search = useSearch(params);
  const hits = useMemo<readonly SearchHit[]>(() => search.data?.hits ?? [], [search.data]);

  // ── Agentic stream ────────────────────────────────────────────────────────
  const agent = useAgentStream();
  // Fire the agent run when in "ask" mode with a query (covers deep links).
  const lastAsked = useRef<string | null>(null);
  useEffect(() => {
    if (params.mode !== "ask") {
      lastAsked.current = null;
      return;
    }
    const q = params.q?.trim() ?? "";
    if (q.length === 0) return;
    if (lastAsked.current === q) return;
    lastAsked.current = q;
    agent.ask(params);
  }, [params, agent]);

  // ── Submit + mode handlers (write to URL) ──────────────────────────────────
  const handleSubmit = useCallback(
    (query: string): void => {
      // Reset paging + close drawer on a fresh query.
      setParams({ q: query || undefined, cursor: undefined, doc: undefined, chunk: undefined });
    },
    [setParams],
  );

  const handleModeChange = useCallback(
    (mode: SearchMode): void => {
      setParams({ mode, cursor: undefined });
    },
    [setParams],
  );

  const handleClearAll = useCallback((): void => {
    const patch: Partial<SearchParams> = {};
    for (const key of FILTER_KEYS) patch[key] = undefined;
    setParams(patch);
  }, [setParams]);

  // ── Document drawer (URL-driven) ────────────────────────────────────────────
  const detail = useMemo<DocumentDetail | null>(() => {
    if (!params.doc) return null;
    const hit = hits.find((h) => h.documentId === params.doc && h.chunkId === params.chunk);
    if (hit) return hitToDetail(hit);
    const cite = agent.citations.find(
      (c) => c.documentId === params.doc && c.chunkId === params.chunk,
    );
    if (cite) return citationToDetail(cite);
    return null;
  }, [params.doc, params.chunk, hits, agent.citations]);

  const openHit = useCallback(
    (hit: SearchHit): void => setParams({ doc: hit.documentId, chunk: hit.chunkId }),
    [setParams],
  );
  const openCitation = useCallback(
    (cite: AgentCitation): void => setParams({ doc: cite.documentId, chunk: cite.chunkId }),
    [setParams],
  );
  const closeDrawer = useCallback(
    (): void => setParams({ doc: undefined, chunk: undefined }),
    [setParams],
  );

  const hasQuery = (params.q?.trim().length ?? 0) > 0;

  return (
    <Box sx={{ maxWidth: 1200, mx: "auto" }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Search
      </Typography>

      <Stack spacing={2} sx={{ mb: 3 }}>
        <SearchBar value={params.q ?? ""} mode={params.mode} onSubmit={handleSubmit} />
        <Stack direction="row" spacing={2} sx={{ alignItems: "center", flexWrap: "wrap" }}>
          <ModeToggle mode={params.mode} onChange={handleModeChange} />
          <SaveSearchButton params={params} />
        </Stack>
      </Stack>

      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 3 }}>
          <Filters
            params={params}
            jurisdictions={JURISDICTIONS}
            onChange={setParams}
            onClearAll={handleClearAll}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 9 }}>
          {params.mode === "ask" ? (
            <AgentChat
              question={params.q ?? ""}
              answer={agent.answer}
              citations={agent.citations}
              isStreaming={agent.isStreaming}
              isDone={agent.isDone}
              errorMessage={agent.errorMessage}
              onOpenCitation={openCitation}
            />
          ) : (
            <ResultsGrid
              hits={hits}
              loading={search.isFetching}
              hasQuery={hasQuery}
              onOpenDocument={openHit}
            />
          )}
          {search.isError ? (
            <Typography role="alert" color="error" sx={{ mt: 2 }}>
              Search failed. Please try again.
            </Typography>
          ) : null}
        </Grid>
      </Grid>

      <DocumentDrawer open={Boolean(detail)} detail={detail} onClose={closeDrawer} />
    </Box>
  );
}

export const Route = createFileRoute("/search")({
  validateSearch: (search: Record<string, unknown>): SearchParams => parseSearchParams(search),
  component: SearchPage,
});
